import {useEffect, useState} from 'react'
import {ProviderValue, useAxios} from '../../../config/axios-config'
import {PageLoader} from '../../util/PageLoader'
import {MenuWeekResponseDTO, Pair} from '../../../model/ResponsesDTO'
import {Button, ConfigProvider, DatePicker, Empty, message, Upload, UploadFile} from 'antd'
import {isMobile} from "react-device-detect";
import {ErrorPage} from '../../error/ErrorPage'
import dayjs, {Dayjs} from 'dayjs'
import isoWeek from "dayjs/plugin/isoWeek";
import isoWeeksInYear from "dayjs/plugin/isoWeeksInYear";
import isLeapYear from "dayjs/plugin/isLeapYear";
import weekOfYear from "dayjs/plugin/weekOfYear";
import 'dayjs/locale/pl'
import {RcFile} from "antd/es/upload";
import {CloseCircleOutlined, DeleteOutlined, FilePdfOutlined, UploadOutlined} from '@ant-design/icons';
import {DinneyResponseStatus} from "../../util/DinneyResponseStatus";
import {UploadRequestOption} from 'rc-upload/lib/interface'
import {MenuWeekErrorPage} from "./MenuWeekEmptyPage";
import {MenuWeekElement} from "./MenuWeekElement";

dayjs.extend(isoWeek)
dayjs.extend(isoWeeksInYear)
dayjs.extend(isLeapYear)
dayjs.extend(weekOfYear)

export const MenuWeekPage = () => {

  const axiosInstance = useAxios() as ProviderValue
  const [pairsOfAccountAndMenuId, setPairsOfAccountAndMenuId] = useState<Pair[]>([])
  const [mondayWeek, setMondayWeek] = useState<Dayjs>(dayjs().startOf("week"))
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const controller = new AbortController();
  const signal = controller.signal



  useEffect(() => {
    setIsLoaded(false)
      axiosInstance.get<MenuWeekResponseDTO>(`/menuweeks/secure/fetch/fordate/${mondayWeek.format('YYYY-MM-DD')}`, {signal})
          .then(response => {
              if (response.status === 200 && response.data.pairsOfAccountAndMenuId != null) {
                  setPairsOfAccountAndMenuId(response.data.pairsOfAccountAndMenuId)
              } else {
                  setIsError(true)
                  console.log("Incorrect response: ", response)
              }
              setIsLoaded(true)
          })
      .catch((e : any) => {
          setIsLoaded(true)
          setIsError(true)
          console.log(e.response?.data?.message === undefined ? e : e.response?.data?.message)
      })
      return () => {
          controller.abort()
      }
  // eslint-disable-next-line
  },[mondayWeek])

  const handleSaveToDatabase = async (e : UploadRequestOption) => {

      const {onSuccess, onError, file} = e

      const formData = new FormData()
      formData.append("dinneyfile", file)

      axiosInstance.post<MenuWeekResponseDTO>(`/menuweeks/secure/save`, formData, {signal})
          .then(response => {
              setPairsOfAccountAndMenuId(response.data.pairsOfAccountAndMenuId)
              onSuccess?.("Zapisano menu")
          })
          .catch((err : any) => {
              onError?.(err as Error, "Error")
              message.error('Nie udało się zapisać menu do bazy. Skontaktuj się z administratorem!')
              console.log(err.response?.data?.message === undefined ? err : err.response?.data?.message)
          })
    return () => {
      controller.abort()
    }
  }

    const handleGetPdf = async (publicId : number) => {

        axiosInstance.get(`/menuweeks/secure/fetch/fordate/pdf/${publicId}`, {
            method: 'GET',
            responseType: 'blob'
        })
            .then(response => {
                if (response === null || response === undefined) {
                    throw new Error("No response")
                } else if (response.status !== 200) {
                    throw new Error(`${response.status} : ${response.statusText}`)
                } else {
                    const file = new Blob([response.data]);
                    const url = URL.createObjectURL(
                        new Blob([file], {type:"application/pdf"})
                    );

                    const filename = response?.headers["content-disposition"].split(';')
                        .find((n : string) => n.includes('filename='))
                        .replace('filename=', '')
                        // eslint-disable-next-line
                        .replaceAll('\"', '')
                        .trim()

                    const link = document.createElement('a');

                    link.href = url;
                    link.setAttribute(
                        'download',
                        filename,
                    );

                    // Append to html link element page
                    document.body.appendChild(link);

                    // Start download
                    link.click();

                    // Clean up and remove the link
                    link.parentNode?.removeChild(link);
                    URL.revokeObjectURL(url);
                }

            })
            .catch((err : any) => {
                message.error('Nie udało się pobrać pliku. Skontaktuj się z administratorem!')
                console.log(err.response?.data?.message === undefined ? err : err.response?.data?.message)
            })
        return () => {
            controller.abort()
        }
    }

    const handleBeforeUpload = (file: RcFile) => {
        const sizeLessThanLimit = file.size / 1024 / 1024 < 1;
        if (sizeLessThanLimit) {
            const newName = `${mondayWeek.format('YYYY-MM-DD')}.pdf`
            message.info('Załadowano menu oraz zmieniono jego nazwę.')
            return new File([file], newName, {type: file.type})
        } else {
            message.error('Menu nie może być większe niż 1MB!')
            return sizeLessThanLimit || Upload.LIST_IGNORE;
        }
    }

  const handleDelete = async (publicId : number) => {
      setIsLoaded(false)
      await axiosInstance.delete<DinneyResponseStatus>(`/menuweeks/secure/delete/${publicId}`, {signal}).then((response) => {
          let isSuccess = response.data === DinneyResponseStatus.DELETED
          if (isSuccess) {
              setPairsOfAccountAndMenuId(prevState => [{first: prevState[0].first, second: -1}])
              message.success('Pomyślnie usunięto menu')
          } else {
              message.error('Nie udało się usunąć menu. Skontaktuj się z administratorem')
          }
          setIsLoaded(true)
      }).catch((e) => {
          console.log(e.response?.data?.message === undefined ? e : e.response?.data?.message)
          setIsLoaded(true)
          setIsError(true)
      })
      return () => {
          controller.abort()
      }
  };

  if (!isLoaded) {
      return (
          <PageLoader />
      )
  }

  if (isError) {
        return (
          <ErrorPage />
        )
  }

  return (
      <div  style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%'}}>
          <ConfigProvider
              theme={{
                  token: {
                      colorPrimary: '#C5B5F2'
                  },
              }}
          >
              <DatePicker size={"large"}
                          defaultValue={mondayWeek}
                          format={`${mondayWeek.startOf('week').format('DD/MM/YYYY')} - ${mondayWeek.endOf('week').subtract(2, 'day').format('DD/MM/YYYY')} (${mondayWeek.week()})`}
                          allowClear={false}
                          onChange={(e)=> setMondayWeek(e.startOf('week'))} picker="week"
                          minDate={dayjs().startOf('week').subtract(1, 'week')}
                          maxDate={dayjs().startOf('week').add(1, 'week')}
                          className={'m-2'}
                          style={{width:`${isMobile ? `100%` : `40%`}`}}/>
          </ConfigProvider>
          <>
              {pairsOfAccountAndMenuId != null && pairsOfAccountAndMenuId.length > 0
                  ?
                  pairsOfAccountAndMenuId.map((pair : Pair, index : number) => (
                    <MenuWeekElement key={index}
                                     pair = {pair}
                                     handleSaveToDatabase = {handleSaveToDatabase}
                                     handleBeforeUpload = {handleBeforeUpload}
                                     handleDelete = {handleDelete}
                                     handleGetPdf = {handleGetPdf}
                    />
                  ))
                  :
                  <MenuWeekErrorPage/>
              }
          </>
      </div>
  )
}